import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/store';
import { loadingActions } from 'features';
import { EnhancedDataGrid } from 'components/common/enhanced/data-grid';
import { ProductsColumns } from 'components/common/enhanced/common-headers/products';
import { productService, typeService } from 'services/api';
import { GridColDef } from '@mui/x-data-grid-premium';
import { IOption } from 'interfaces';

interface SupplierLite {
  id: number;
  code: string;
  company_name?: string;
}

interface SupplierProductsDialogProps {
  open: boolean;
  suppliers: SupplierLite[];
  supplier: SupplierLite | null;
  onClose: () => void;
}

export const SupplierProductsDialog = ({
  open,
  suppliers,
  supplier,
  onClose
}: SupplierProductsDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sap_active = useAppSelector((state) => state.auth.sap_active);

  const datagridRefresh = useRef(null);
  const [types, setTypes] = useState<IOption[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierLite | null>(supplier);

  useEffect(() => {
    if (!open) return;
    dispatch(loadingActions.startLoading());
    typeService
      .getAllBaseInformation()
      .then((res) => setTypes(res ?? []))
      .finally(() => dispatch(loadingActions.stopLoading()));
  }, [dispatch, open]);

  useEffect(() => {
    if (!open) return;
    // When opening, keep preselected supplier if provided; otherwise auto-select if only one supplier.
    if (supplier?.id) {
      setSelectedSupplier(supplier);
      return;
    }
    if (suppliers.length === 1) {
      setSelectedSupplier(suppliers[0]);
    } else {
      setSelectedSupplier(null);
    }
  }, [open, supplier, suppliers]);

  // NOTE: `ProductsColumns` uses hooks internally (useTranslation), so it must not be
  // called inside `useMemo` or any conditional path; call it on every render.
  const getContractLines = (params) => params.row.contract_lines;
  const columns = ProductsColumns(
    getContractLines,
    types.map((x) => x.code),
    datagridRefresh.current,
    sap_active,
    false
  ) as GridColDef[];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>{t('global.products')}</DialogTitle>
      <DialogContent>
        {suppliers.length > 1 ? (
          <Autocomplete
            sx={{ mt: 1, mb: 2, maxWidth: 480 }}
            options={suppliers}
            value={selectedSupplier}
            onChange={(_, v) => setSelectedSupplier(v)}
            getOptionLabel={(o) => `${o.code} - ${o.company_name ?? ''}`.trim()}
            renderInput={(params) => (
              <TextField {...params} label={t('global.supplier')} size="small" />
            )}
          />
        ) : null}

        {selectedSupplier?.id ? (
          <EnhancedDataGrid
            refresh={datagridRefresh}
            service={productService}
            columns={columns}
            initialParams={{ supplier: String(selectedSupplier.id) }}
            disableExport={true}
            disableEdit={true}
            disableDelete={true}
            dense
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupplierProductsDialog;
